package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.FontStyle
import top.wcpe.allincore.ui.runtime.element.Text
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.internal.time.currentTimeMillis
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.theme.ControlTheme
import top.wcpe.allincore.ui.runtime.theme.FontAccess
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess
import kotlin.concurrent.Volatile
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic
import kotlin.random.Random

/**
 * 文本渲染器：支持单行/多行、文本对齐、字体样式。
 *
 * 多行模式下既按 `\n` 显式断行，也按 box 宽度自动换行（与 ButtonRenderer 同算法）；
 * 单行模式 `\n` 替换为空格。
 */
@Suppress("MagicNumber", "LongParameterList")
class TextRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val t = element as Text
        val raw = t.text()
        if (raw.isNullOrEmpty()) {
            return
        }
        // 逐字乱码揭示：在绘制前把文本替换为当前帧的乱码态串，再走原有排版/绘制路径。
        // decipher=false 时完全不进入该分支，普通文本零回归。
        val text = if (t.decipher()) decipherText(t, raw) else raw

        val theme = ThemeAccess.current()
        val scale = t.effectiveScale(theme)
        // 行高优先取实例级 lineHeight；未设置时回退 effectiveLineHeight（与 Text.measurer 一致，
        // 确保布局阶段预估的高度与渲染阶段实际占用的高度一致）。
        val lineStep = t.lineHeight() ?: t.effectiveLineHeight(theme)
        val font = FontAccess.resolve(t.font())

        // 元素/页面/全局都没显式声明字体名时（effectiveName 回落到 vanilla 默认），
        // 改用主题级 fontFamily：默认主题 fontFamily 同为 minecraft:default，故未自定义字体的主题零回归。
        val fontName = resolveFontName(font, theme)
        val shadow = font.effectiveShadow()
        val bold = font.effectiveBold()
        val italic = font.effectiveItalic()

        // 富文本：richText=true 且平台支持时，整段交给平台内联富文本绘制后返回；
        // 平台不支持或 richText=false 时完全走下方原路径（零回归）。
        // 注：richText 与 decipher/letterSpacing 互斥——三者同时为真时 richText 优先。
        if (renderRichTextIfHandled(t, bridge, box, text, scale, fontName, shadow, bold, italic, lineStep)) {
            return
        }

        // 字间距按 scale 缩放；null/0 时为 0，逐字符绘制路径不被触发（零回归、性能不变）。
        val letterAdvance = (t.letterSpacing() ?: 0f) * scale
        val perChar = letterAdvance != 0f

        if (!t.multiline()) {
            // 单行：\n 替换为空格
            val single = text.replace('\n', ' ')
            val lineW = lineWidth(single, bridge, scale, fontName, bold, perChar, letterAdvance)
            val x = alignX(box, t.textAlign(), lineW)
            drawTextLine(
                bridge, single, x, box.y(), t.color(), scale, fontName,
                shadow, bold, italic, perChar, letterAdvance,
            )
            drawDecoration(bridge, t.textDecoration(), x, box.y(), lineW, lineStep, t.color(), scale)
            return
        }

        // 多行：先按 box 宽度自动换行（含显式 \n 断行），再逐行绘制
        // 直接走共享 TextWrapper 逐字符断行算法（中文友好，原经 ButtonRenderer.wrapText 委托的同一实现）；
        // 测宽用真实 fontName + bold，与最终 drawText 的字体宽度一致，避免粗体或自定义字体下换行错位。
        val lines = TextWrapper.wrap(text, box.width()) { s -> bridge.textWidth(s, scale, fontName, bold) }
        lines.forEachIndexed { i, line ->
            val lineW = lineWidth(line, bridge, scale, fontName, bold, perChar, letterAdvance)
            val x = alignX(box, t.textAlign(), lineW)
            val y = box.y() + i * lineStep
            drawTextLine(
                bridge, line, x, y, t.color(), scale, fontName,
                shadow, bold, italic, perChar, letterAdvance,
            )
            drawDecoration(bridge, t.textDecoration(), x, y, lineW, lineStep, t.color(), scale)
        }
    }

    /** 富文本同样支持 textAlign：center/right 时按富文本实测宽度把绘制原点 x 居中 / 右对齐。 */
    private fun renderRichTextIfHandled(
        t: Text,
        bridge: UiRenderBridge,
        box: Box,
        text: String,
        scale: Float,
        fontName: String,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
        lineStep: Float,
    ): Boolean {
        if (!t.richText() || !bridge.supportsRichText()) {
            return false
        }
        // left（默认）直接用 box.x()，不触发额外度量（零回归、热路径不变）。
        val rx = richTextX(t, bridge, box, text, scale, fontName, bold)
        bridge.drawRichText(
            text, rx, box.y(), t.color(), scale, fontName,
            shadow, bold, italic, box.width(), t.multiline(), lineStep,
        )
        return true
    }

    private fun richTextX(
        t: Text,
        bridge: UiRenderBridge,
        box: Box,
        text: String,
        scale: Float,
        fontName: String,
        bold: Boolean,
    ): Float {
        val align = t.textAlign()
        if (align != "center" && align != "right") {
            return box.x()
        }
        val dim = bridge.measureRichText(text, scale, fontName, bold, box.width(), t.multiline())
        val w = if (dim != null && dim.isNotEmpty()) dim[0] else 0f
        if (w <= 0f || w >= box.width()) {
            return box.x()
        }
        return if (align == "center") box.x() + (box.width() - w) / 2f else box.x() + box.width() - w
    }

    /**
     * 计算一行文本的像素宽度：含字距模式下为「各字符宽度之和 + (字符数-1)*字距」，
     * 否则直接取整串测宽（与原行为一致，零回归）。
     */
    private fun lineWidth(
        line: String,
        bridge: UiRenderBridge,
        scale: Float,
        fontName: String,
        bold: Boolean,
        perChar: Boolean,
        letterAdvance: Float,
    ): Float {
        if (!perChar) {
            return bridge.textWidth(line, scale, fontName, bold)
        }
        if (line.isEmpty()) {
            return 0f
        }
        var total = 0f
        for (c in line) {
            total += bridge.textWidth(c.toString(), scale, fontName, bold)
        }
        // 字距只加在字符之间，末字符后不补
        return total + (line.length - 1) * letterAdvance
    }

    /**
     * 绘制一行文本：含字距模式逐字符绘制（每步前进「单字符宽 + 字距」，空白字符同样前进），
     * 否则整串绘制（保留原 color/scale/font/shadow/bold/italic，性能不变）。
     */
    private fun drawTextLine(
        bridge: UiRenderBridge,
        line: String,
        x: Float,
        y: Float,
        color: Int,
        scale: Float,
        fontName: String,
        shadow: Boolean,
        bold: Boolean,
        italic: Boolean,
        perChar: Boolean,
        letterAdvance: Float,
    ) {
        if (!perChar) {
            bridge.drawText(line, x, y, color, scale, fontName, shadow, bold, italic)
            return
        }
        var cursor = x
        for (c in line) {
            val ch = c.toString()
            bridge.drawText(ch, cursor, y, color, scale, fontName, shadow, bold, italic)
            // 空格等空白字符也按其实际测宽前进；中文字符 textWidth 更宽，按实际值前进即可
            cursor += bridge.textWidth(ch, scale, fontName, bold) + letterAdvance
        }
    }

    /**
     * 按 textDecoration 在文本下方画下划线、在文本垂直中部画删除线。
     *
     * 取值 "underline" / "line-through" / "underline line-through"（含 none/null 时直接返回）。
     * 线宽 thickness = max(1, scale)，颜色取文本色，跨整行文本宽度。
     */
    private fun drawDecoration(
        bridge: UiRenderBridge,
        decoration: String?,
        textX: Float,
        lineY: Float,
        textWidth: Float,
        lineStep: Float,
        color: Int,
        scale: Float,
    ) {
        if (decoration.isNullOrEmpty() || textWidth <= 0f) {
            return
        }
        if (decoration.contains("none")) {
            return
        }
        val thickness = scale.coerceAtLeast(1f)
        if (decoration.contains("underline")) {
            // 下划线落在文本基线下方（接近行底）
            val underlineY = lineY + lineStep - thickness
            bridge.fillRect(textX, underlineY, textWidth, thickness, color)
        }
        if (decoration.contains("line-through")) {
            // 删除线落在行高垂直中部
            val strikeY = lineY + (lineStep - thickness) / 2f
            bridge.fillRect(textX, strikeY, textWidth, thickness, color)
        }
    }

    /**
     * 计算当前帧的乱码态文本。
     *
     * 首帧锁定起始时刻 `decipherStartMs`；文本长度变化时重算固定揭示次序 `decipherOrder`
     * （以元素身份哈希为种子，保证同一元素稳定、不同元素互异）。按进度 `p` 算出已定形字数，
     * 交由 [DecipherText.decipherFrame] 生成显示串。`p>=1` 后定形，返回原文。
     */
    private fun decipherText(t: Text, original: String): String {
        val now = clock()
        // 首帧锁定起始时刻
        if (t.decipherStartMs() < 0L) {
            t.decipherStartMs(now)
        }
        val length = original.length
        // 文本长度变化（含首次）时重算揭示次序，种子取元素身份哈希保证 per-element 稳定且互异
        if (t.decipherOrder() == null || t.decipherOrderLength() != length) {
            // 种子取元素默认 hashCode（UIElement 未重写，JVM 上即身份哈希语义），保证 per-element 稳定且互异
            t.decipherOrder(DecipherText.buildOrder(length, t.hashCode().toLong()))
        }
        val duration = t.decipherDurationMs()
        // 时长非正时视作瞬间完成，直接定形
        val p = if (duration <= 0f) 1f else (now - t.decipherStartMs()) / duration
        if (p >= 1f) {
            return original
        }
        val revealed = DecipherText.revealedCount(length, p.coerceAtLeast(0f))
        // decipherFrame 对 null 文本原样返回，但此处 original 恒非空（render 已过 isNullOrEmpty）；
        // ?: original 兜底仅为满足空安全，正常路径不触发。
        return DecipherText.decipherFrame(original, t.decipherCharset(), t.decipherOrder(), revealed, JUMBLE_RANDOM)
            ?: original
    }

    /**
     * 按对齐方式计算起始 X。textW 为「含字距」的整行宽度（由 [lineWidth] 预算），
     * center/right 对齐时据此偏移，保证逐字符绘制下对齐仍准确。
     */
    private fun alignX(box: Box, align: String?, textW: Float): Float = when (align) {
        "center" -> box.x() + (box.width() - textW) / 2f
        "right" -> box.x() + box.width() - textW
        // left（默认）
        else -> box.x()
    }

    /**
     * 解析最终字体名：元素/页面/全局任一层显式声明了字体名则用之；
     * 否则（`effectiveName()` 回落到 [FontStyle.DEFAULT_NAME]）改用主题级 `fontFamily`。
     * 默认主题 fontFamily 同为 minecraft:default，故非自定义字体主题零回归。
     */
    private fun resolveFontName(font: FontStyle, theme: ControlTheme): String {
        val name = font.effectiveName()
        return if (FontStyle.DEFAULT_NAME == name) theme.fontFamily else name
    }

    companion object {
        /** 时钟来源：默认 [currentTimeMillis]，测试可注入。 */
        @Volatile
        private var clock: () -> Long = { currentTimeMillis() }

        /** 乱码取字随机源：渲染线程单线程访问，每帧重用同一实例制造跳动乱码。 */
        private val JUMBLE_RANDOM = Random(currentTimeMillis())

        /** 注入时钟（仅供测试 / 平台层模拟）。 */
        @JvmStatic
        @JvmName("setClock")
        internal fun setClock(supplier: (() -> Long)?) {
            clock = supplier ?: { currentTimeMillis() }
        }

        /** 还原默认时钟。 */
        @JvmStatic
        @JvmName("resetClock")
        internal fun resetClock() {
            clock = { currentTimeMillis() }
        }
    }
}
